/**
 * DatabaseFormFields — per-engine + per-mode field clusters consumed by
 * `DatabaseEditFormBody`.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice G1.
 */
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DatabaseEditMode, DatabaseFormValues } from './databaseFormHelpers';

/**
 * Radix Select forbids `value=""`, but `''` is exactly what the form values use
 * for "— default —" / "— no change —" (see `buildDatabaseDefFromForm` /
 * `buildDatabaseChangeFromForm`, which skip the field when it is `''`). Map
 * `'' → UNSET` on the way in and `UNSET → ''` on the way out so the commit
 * builders keep seeing the same empty-string sentinel they always have.
 */
const UNSET = '__unset__' as const;

export interface DatabaseFormFieldsProps {
  readonly engine: SqlEngine;
  readonly mode: DatabaseEditMode;
  readonly values: DatabaseFormValues;
  readonly testId: string;
  readonly update: <K extends keyof DatabaseFormValues>(
    key: K,
    value: DatabaseFormValues[K],
  ) => void;
}

export function DatabaseFormFields(props: DatabaseFormFieldsProps) {
  const { engine, mode, values, testId, update } = props;

  if (mode === 'create') {
    return (
      <div className="flex flex-col gap-3">
        {engine === 'mysql' ? (
          <label className="flex items-center gap-2 text-[11px]" data-testid={`${testId}-if-not-exists-label`}>
            <Checkbox
              data-testid={`${testId}-if-not-exists`}
              checked={values.ifNotExists}
              onCheckedChange={(checked) => update('ifNotExists', checked === true)}
            />
            IF NOT EXISTS
          </label>
        ) : null}
        {engine === 'mysql' ? (
          <label className="flex items-center gap-2 text-[11px]" data-testid={`${testId}-mysql-schema-label`}>
            <Checkbox
              data-testid={`${testId}-mysql-schema`}
              checked={values.mysqlSchema}
              onCheckedChange={(checked) => update('mysqlSchema', checked === true)}
            />
            Emit `SCHEMA` keyword (synonym for DATABASE)
          </label>
        ) : null}
        {engine === 'mssql' ? <MssqlCreateFields values={values} testId={testId} update={update} /> : null}
        {engine === 'postgres' ? <PgsqlCreateFields values={values} testId={testId} update={update} /> : null}
        {engine === 'mysql' ? <MysqlCreateFields values={values} testId={testId} update={update} /> : null}
        {engine === 'oracle' ? <OracleCreateFields values={values} testId={testId} update={update} /> : null}
      </div>
    );
  }

  // alter
  return (
    <div className="flex flex-col gap-3">
      {engine === 'mssql' ? <MssqlAlterFields values={values} testId={testId} update={update} /> : null}
      {engine === 'postgres' ? <PgsqlAlterFields values={values} testId={testId} update={update} /> : null}
      {engine === 'mysql' ? <MysqlCreateFields values={values} testId={testId} update={update} alterMode /> : null}
      {engine === 'oracle' ? <OraclePdbStateFields values={values} testId={testId} update={update} /> : null}
    </div>
  );
}

interface FieldsProps {
  readonly values: DatabaseFormValues;
  readonly testId: string;
  readonly update: <K extends keyof DatabaseFormValues>(
    key: K,
    value: DatabaseFormValues[K],
  ) => void;
  readonly alterMode?: boolean;
}

function MssqlCreateFields({ values, testId, update }: FieldsProps) {
  return (
    <>
      <UnsetSelect
        id={`${testId}-mssql-containment`}
        label="Containment"
        unsetLabel="— default —"
        value={values.mssqlContainment}
        options={[['none', 'NONE'], ['partial', 'PARTIAL']]}
        onChange={(v) => update('mssqlContainment', v as DatabaseFormValues['mssqlContainment'])}
      />
      <TextField id={`${testId}-mssql-collate`} label="COLLATE" value={values.mssqlCollate}
        onChange={(v) => update('mssqlCollate', v)} />
    </>
  );
}

function PgsqlCreateFields({ values, testId, update }: FieldsProps) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <TextField id={`${testId}-pgsql-owner`} label="OWNER" value={values.pgsqlOwner}
        onChange={(v) => update('pgsqlOwner', v)} />
      <TextField id={`${testId}-pgsql-template`} label="TEMPLATE" value={values.pgsqlTemplate}
        onChange={(v) => update('pgsqlTemplate', v)} />
      <TextField id={`${testId}-pgsql-encoding`} label="ENCODING" value={values.pgsqlEncoding}
        onChange={(v) => update('pgsqlEncoding', v)} />
      <TextField id={`${testId}-pgsql-tablespace`} label="TABLESPACE" value={values.pgsqlTablespace}
        onChange={(v) => update('pgsqlTablespace', v)} />
      <TextField id={`${testId}-pgsql-lc-collate`} label="LC_COLLATE" value={values.pgsqlLcCollate}
        onChange={(v) => update('pgsqlLcCollate', v)} />
      <TextField id={`${testId}-pgsql-lc-ctype`} label="LC_CTYPE" value={values.pgsqlLcCtype}
        onChange={(v) => update('pgsqlLcCtype', v)} />
      <TextField id={`${testId}-pgsql-conn-limit`} label="CONNECTION LIMIT"
        value={values.pgsqlConnectionLimit}
        onChange={(v) => update('pgsqlConnectionLimit', v)} />
    </div>
  );
}

function MysqlCreateFields({ values, testId, update }: FieldsProps) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <TextField id={`${testId}-mysql-charset`} label="CHARACTER SET" value={values.mysqlCharacterSet}
        onChange={(v) => update('mysqlCharacterSet', v)} />
      <TextField id={`${testId}-mysql-collate`} label="COLLATE" value={values.mysqlCollate}
        onChange={(v) => update('mysqlCollate', v)} />
    </div>
  );
}

function OracleCreateFields({ values, testId, update }: FieldsProps) {
  return (
    <>
      <TextField id={`${testId}-oracle-admin-user`} label="ADMIN USER (required)"
        value={values.oracleAdminUser} onChange={(v) => update('oracleAdminUser', v)} />
      <TextField id={`${testId}-oracle-admin-password`} label="IDENTIFIED BY (required)"
        value={values.oracleAdminPassword} onChange={(v) => update('oracleAdminPassword', v)} />
      <TextField id={`${testId}-oracle-file-name-convert`} label="FILE_NAME_CONVERT (verbatim)"
        value={values.oracleFileNameConvertText}
        onChange={(v) => update('oracleFileNameConvertText', v)} />
    </>
  );
}

function MssqlAlterFields({ values, testId, update }: FieldsProps) {
  return (
    <>
      <TextField id={`${testId}-mssql-new-name`} label="MODIFY NAME (rename)"
        value={values.mssqlNewName} onChange={(v) => update('mssqlNewName', v)} />
      <UnsetSelect
        id={`${testId}-mssql-recovery`}
        label="SET RECOVERY"
        unsetLabel="— no change —"
        value={values.mssqlRecovery}
        options={[['full', 'FULL'], ['simple', 'SIMPLE'], ['bulk-logged', 'BULK_LOGGED']]}
        onChange={(v) => update('mssqlRecovery', v as DatabaseFormValues['mssqlRecovery'])}
      />
      <TextField id={`${testId}-mssql-compat-level`} label="SET COMPATIBILITY_LEVEL"
        value={values.mssqlCompatibilityLevel}
        onChange={(v) => update('mssqlCompatibilityLevel', v)} />
    </>
  );
}

function PgsqlAlterFields({ values, testId, update }: FieldsProps) {
  return (
    <>
      <TextField id={`${testId}-pgsql-rename-to`} label="RENAME TO"
        value={values.pgsqlRenameTo} onChange={(v) => update('pgsqlRenameTo', v)} />
      <TextField id={`${testId}-pgsql-owner-to`} label="OWNER TO"
        value={values.pgsqlOwnerTo} onChange={(v) => update('pgsqlOwnerTo', v)} />
    </>
  );
}

function OraclePdbStateFields({ values, testId, update }: FieldsProps) {
  return (
    <UnsetSelect
      id={`${testId}-oracle-pdb-open`}
      label="PDB state"
      unsetLabel="— no change —"
      value={values.oraclePdbOpen}
      options={[['open', 'OPEN'], ['close', 'CLOSE IMMEDIATE']]}
      onChange={(v) => update('oraclePdbOpen', v as DatabaseFormValues['oraclePdbOpen'])}
    />
  );
}

interface UnsetSelectProps {
  readonly id: string;
  /** Visible field label — also the `<Label htmlFor>` target for `id`. */
  readonly label: string;
  /** Label of the `''` ("no change" / "default") choice. */
  readonly unsetLabel: string;
  readonly value: string;
  readonly options: ReadonlyArray<readonly [value: string, label: string]>;
  /** Receives `''` for the unset choice — never the internal sentinel. */
  readonly onChange: (next: string) => void;
}

/** Label + Select for the tri-state "unset / A / B" database options. */
function UnsetSelect({ id, label, unsetLabel, value, options, onChange }: UnsetSelectProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-[11px]">{label}</Label>
      <Select
        value={value === '' ? UNSET : value}
        onValueChange={(v) => onChange(v === UNSET ? '' : v)}
      >
        <SelectTrigger id={id} data-testid={id} className="h-7 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={UNSET}>{unsetLabel}</SelectItem>
          {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

interface TextFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
}

function TextField({ id, label, value, onChange }: TextFieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-[11px]">{label}</Label>
      <Input id={id} data-testid={id} className="h-7 text-xs"
        value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
